import React, { useMemo, useRef, useState } from "react";
import { Chip } from "components/common/Chip";
import { DropdownMenu } from "components/common/DropdownMenu";
import { DropdownMenuItem } from "components/common/DropdownMenuItem";
import { TextInput } from "components/common/TextInput";

export interface Person {
  email: string;
  displayName?: string | null;
}

/// One person in a picker list, on ONE line. The name-over-address stack burst
/// `item-height` and read a size larger than every other list row in the app.
///
/// The ADDRESS is what identifies someone, so it never truncates first — the
/// name gives way. Both carry a title, because two colleagues can share a long
/// domain and the menu is still capped.
export const PersonOption: React.FC<Person> = ({ email, displayName }) => (
  <span
    className="flex min-w-0 flex-1 items-baseline gap-2 text-left"
    title={displayName ? `${displayName}\n${email}` : email}
  >
    <span className="truncate text-fg-regular">{displayName || email}</span>
    {displayName && (
      <span className="shrink-0 truncate text-fg-muted-regular-small">
        {email}
      </span>
    )}
  </span>
);

const matchesPerson = (person: Person, needle: string) =>
  `${person.email} ${person.displayName ?? ""}`.toLowerCase().includes(needle);

interface PersonPickerProps {
  directory: Person[];
  selected: Person | null;
  onSelect: (person: Person | null) => void;
  placeholder?: string;
  ariaLabel: string;
  className?: string;
}

/// Pick exactly one person from a roster. Typing narrows; only a click or Enter
/// SELECTS, and the caller acts on the selection — never on the raw text. A
/// half-typed address matches people the user never meant, so filtering on
/// keystrokes answers a question nobody asked.
export const PersonPicker: React.FC<PersonPickerProps> = ({
  directory,
  selected,
  onSelect,
  placeholder,
  ariaLabel,
  className,
}) => {
  const [query, setQuery] = useState("");
  const [dismissed, setDismissed] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const anchorRef = useRef<HTMLDivElement>(null);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return directory.filter((p) => matchesPerson(p, needle)).slice(0, 8);
  }, [directory, query]);

  const open = matches.length > 0 && !dismissed;

  const pick = (person: Person) => {
    onSelect(person);
    setQuery("");
    setDismissed(true);
  };

  if (selected) {
    return (
      <Chip
        label={selected.email}
        active
        onClose={() => onSelect(null)}
        ariaLabel={`${ariaLabel}: ${selected.email}. Clear`}
        className={className}
      />
    );
  }

  return (
    <div ref={anchorRef} className={className}>
      <TextInput
        value={query}
        onChange={(v) => {
          setQuery(v);
          setDismissed(false);
          setHighlight(0);
        }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((i) => (i + 1) % matches.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((i) => (i - 1 + matches.length) % matches.length);
          } else if (e.key === "Enter") {
            e.preventDefault();
            pick(matches[Math.min(highlight, matches.length - 1)]);
          } else if (e.key === "Escape") {
            setDismissed(true);
          }
        }}
        placeholder={placeholder}
        ariaLabel={ariaLabel}
        // The browser's own address autofill covers the list with a menu the
        // app does not control and cannot style.
        autoComplete="off"
      />
      <DropdownMenu
        anchorRef={anchorRef}
        isOpen={open}
        onClose={() => setDismissed(true)}
        // The default placement pins the menu to the anchor's width, and the
        // anchor is one of three things sharing a row — too narrow to show a
        // name and an address at once. Grow to the content, floor at the
        // anchor, and stop before the menu becomes the widest thing on screen.
        style={{
          width: "max-content",
          minWidth: anchorRef.current?.getBoundingClientRect().width,
          maxWidth: "min(32rem, 90vw)",
        }}
      >
        {matches.map((person, index) => (
          <DropdownMenuItem
            key={person.email}
            isSelected={index === highlight}
            onClick={() => pick(person)}
          >
            <PersonOption {...person} />
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
    </div>
  );
};
